"use client";

import posthog from "posthog-js";
import { PostHogProvider, usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";

import { useSession } from "@/hooks/use-session";
import { api } from "@/lib/trpc/react";

/**
 * Identifies the current user with PostHog when logged in.
 * Resets identification when logged out.
 *
 * While an admin is impersonating another user, we keep event capture opted out
 * so nothing is attributed to the impersonated user's profile, but we still
 * point feature-flag evaluation AT them so the UI reflects what they would see
 * (lets us QA per-user flags via impersonation). Opt-out gates capture(), not
 * flag loading, so the two goals don't conflict.
 */
function PostHogIdentifier() {
  const posthogClient = usePostHog();
  const { user, session, isLoading } = useSession();
  const isImpersonating = Boolean(session?.session?.impersonatedBy);
  const prevUserIdRef = useRef<string | null>(null);

  const { data: identifyContext } =
    api.user.getAnalyticsIdentifyContext.useQuery(undefined, {
      enabled: Boolean(user?.id) && !isImpersonating,
      staleTime: 0,
      refetchOnWindowFocus: true,
    });

  useEffect(() => {
    if (isImpersonating) {
      // Keep events off the impersonated user's profile, but evaluate feature
      // flags AS them so the UI matches what they would see. opt-out gates
      // capture(), not flag loading. reset() clears the admin identity so
      // identify() can switch the distinct id (its $identify is swallowed by
      // the opt-out); setPersonPropertiesForFlags lets email-targeted release
      // conditions match too.
      posthogClient.reset();
      posthogClient.opt_out_capturing();
      if (user?.id) {
        posthogClient.identify(user.id);
        if (user.email) {
          posthogClient.setPersonPropertiesForFlags({ email: user.email });
        }
        posthogClient.reloadFeatureFlags();
      }
      return;
    }

    if (posthogClient.has_opted_out_capturing()) {
      posthogClient.opt_in_capturing();
    }

    if (isLoading) {
      return;
    }

    if (!user?.id) {
      if (prevUserIdRef.current) {
        posthogClient.reset();
        prevUserIdRef.current = null;
      }
      return;
    }

    const currentDistinctId = posthogClient.get_distinct_id();
    const isStaleIdentified =
      currentDistinctId &&
      currentDistinctId !== user.id &&
      posthogClient.persistence?.props?.$user_state === "identified";
    if (isStaleIdentified) {
      posthogClient.reset();
    }

    // Associate the organization group for analytics so events are attributed
    // to the active org. This is for attribution only: org-scoped flags are
    // evaluated server-side keyed on the org id (ENG-725), so nothing here
    // registers the org id as a group property or forces a flag reload.
    if (identifyContext?.active_organization) {
      posthogClient.group(
        "organization",
        identifyContext.active_organization.id,
        {
          name: identifyContext.active_organization.name,
        },
      );
    }

    posthogClient.identify(user.id, {
      email: user.email,
      name: user.name,
      ...(identifyContext
        ? {
            user_type: identifyContext.user_type,
            signup_source: identifyContext.signup_source,
            signup_utm_source: identifyContext.signup_utm_source,
            signup_utm_medium: identifyContext.signup_utm_medium,
            signup_utm_campaign: identifyContext.signup_utm_campaign,
            signup_utm_content: identifyContext.signup_utm_content,
            signup_utm_term: identifyContext.signup_utm_term,
            onboarding_flow_status: identifyContext.onboarding_flow_status,
          }
        : {}),
    });
    prevUserIdRef.current = user.id;
  }, [
    posthogClient,
    isImpersonating,
    isLoading,
    user?.id,
    user?.email,
    user?.name,
    identifyContext,
  ]);

  return null;
}

/**
 * Combined PostHog provider + identifier wrapper.
 *
 * Initialization happens in instrumentation-client.ts (runs before hydration,
 * the documented Next.js setup); this component only provides the client to
 * the tree and keeps the identified user in sync. Do NOT call posthog.init()
 * here; it would double-init.
 */
export function PostHogWrapper({ children }: { children: React.ReactNode }) {
  return (
    <PostHogProvider client={posthog}>
      <PostHogIdentifier />
      {children}
    </PostHogProvider>
  );
}
